import React, { StrictMode, Suspense, lazy } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import { getSignUpState } from '@mobi/account/Areas/SignUp/Store'
import { signupTracker } from '@mobi/account/Common/Analytics'
import ModalPageHeader from '@mobi/account/Common/ModalPageHeader'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { useDisableAppHeaderRender } from '@core/Areas/AppHeader/hooks/useDisableAppHeaderRender'
import { openDeposit } from '@core/Areas/Deposit'
import { setTrustedDeviceDetails } from '@core/Areas/Login/Store'
import { attemptLogin, showLogin } from '@core/Areas/Login/helpers'
import { useDisableTabNavigationRender } from '@core/Areas/Navigation/Hooks/useDisableTabNavigationRender'
import { SignUpVerificationPage } from '@core/Areas/Settings/Components/MFASettings/Pages/SignUpVerificationPage'
import { MainContainer } from '@core/Components/Containers'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { useObservableImmutable } from '@core/Utils/hooks'
import { useAppSeo } from '@core/Utils/hooks/useAppSeo'

const SignUpPackage = lazy(() => import('@mobi/account/Areas/SignUp/Components/SignUpPage'))

// needs to be memoed, because it sends the verification code on mount, and
// we don't want this to be spammed
const SignupVerificationPageMemo = React.memo(SignUpVerificationPage)

export const SignUpPage = () => {
  useAppSeo({
    title: 'Sign Up | TABtouch',
    description: 'Sign up for a TABtouch account and start betting today.',
  })
  const state = useSelector(getSignUpState)
  const dispatch = useDispatch()
  useDisableAppHeaderRender()
  useDisableTabNavigationRender()
  const { isLoggedIn } = useObservableImmutable(userAccountState$, ['isLoggedIn'])
  const availableCampaigns = useObservableImmutable(userAccountState$, ['campaigns']).campaigns
  const firstLiveCampaign: Campaign | undefined = availableCampaigns?.find(x => x.isLive)

  const signUpAttemptLogin = async (request: Parameters<typeof attemptLogin>[0]) => {
    const result = await attemptLogin(request)

    if (result.status === 'SUCCESSFUL') {
      if (result.newDeviceKey && result.newDeviceGroupKey) {
        dispatch(
          setTrustedDeviceDetails({
            accountIdentifier: request.accountIdentifier,
            deviceDetails: {
              deviceKey: result.newDeviceKey,
              deviceGroupKey: result.newDeviceGroupKey,
              deviceSecret: request.deviceSecret,
              isRemembered: false,
            },
          })
        )
      }
    }

    return result
  }

  return (
    <StrictMode>
      <Suspense fallback={<Spinner key='signup-loader' />}>
        <ModalPageHeader
          onClickNeedHelp={() =>
            signupTracker({
              interaction_type: 'Need Assistance',
              signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
              location: 'Header',
            })
          }
          onClickHome={() =>
            signupTracker({
              interaction_type: 'Go Back to Home Page',
              signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
              location: 'Header',
              signUpStarted: state.hasUserStartedSignup,
            })
          }
        />
        <MainContainer data-tid-sign-up background='white'>
          <SignUpPackage
            isLoggedIn={isLoggedIn}
            attemptLogin={signUpAttemptLogin}
            showLogin={showLogin}
            campaign={firstLiveCampaign}
            openDeposit={openDeposit}
            MfaVerificationPage={SignupVerificationPageMemo}
          />
        </MainContainer>
      </Suspense>
    </StrictMode>
  )
}
